/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { ChosenWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { AvailableWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';

import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessageService } from '@src/app/shared/message.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';

export interface EditWidgetDialogData {
  chosenWidget: ChosenWidget;
  showingWidgets: ChosenWidget[];
  allWidgets: AvailableWidget[];
  changeWidget: (aw: AvailableWidget) => void;
  refreshWidgets: () => void;
}

@Component({
    selector: 'app-edit-widget',
    imports: [FormsModule, MatSliderModule, MatButtonModule, DialogBannerComponent, MatDialogModule, FormsModule, MatTooltipModule],
    templateUrl: './edit-widget.component.html',
    styleUrl: './edit-widget.component.scss'
})
export class EditWidgetDialogComponent {
  data = inject<EditWidgetDialogData>(MAT_DIALOG_DATA);
  msgSvc = inject(MessageService);
  screenSvc = inject(IsNarrowService);
  cds = inject(ConfirmDialogService);
  chosenWidget = { ...this.data.chosenWidget };
  availableWidget = this.data.allWidgets.find(aw => aw.code === this.chosenWidget.code);
  availableWidgets: AvailableWidget[] = this.data.allWidgets.filter(aw => !this.data.showingWidgets.find(sw => aw.code === sw.code));

  availableHeights = [1, 2, 3, 4, 5, 6, 7];
  availableWidths = [1, 2, 3, 4, 5, 6, 7];

  dialogRef = inject(MatDialogRef<EditWidgetDialogComponent>);

  constructor() {
    if (!this.chosenWidget.heightWeight) this.chosenWidget.heightWeight = 2;
    if (!this.chosenWidget.widthWeight) this.chosenWidget.widthWeight = 2;
    if (!this.availableWidget) {
      this.msgSvc.showErrorMessage($localize`Fatal error - ${this.chosenWidget.code} not found`);
    }
    if (this.screenSvc.isPhone()) {
      this.availableWidths = [1,2]
    }
  }

  save() {
    this.dialogRef.close(this.data);
  }

  close() {
    this.dialogRef.close();
  }

  widgetSelected(code: string) {
    if (code) {
      const ac = this.data.allWidgets.find(aw => aw.code === code);
      const title = $localize`Change Widget?`
      const msg = $localize`Replace the '${this.availableWidget.name}' with the '${ac.name}' widget?`
      this.cds.open(title, msg, () => {
        this.data.changeWidget(ac);
        this.data.refreshWidgets();
        this.dialogRef.close();
      })
    }
  }
  removeChart() {
    const title = $localize `Remove Widget?`;
    const msg = $localize `Remove the ${this.availableWidget.name} widget from your dashboard?`;
    this.cds.open(title, msg, () => {
      this.data.showingWidgets.splice(this.data.showingWidgets.indexOf(this.data.chosenWidget), 1);
      this.data.refreshWidgets();
      this.dialogRef.close()
    })
  }

  resetSize() {
    this.chosenWidget.heightWeight = this.data.chosenWidget.heightWeight;
    this.chosenWidget.widthWeight = this.data.chosenWidget.widthWeight;
  }

  showSize(width, height) {
    this.chosenWidget.heightWeight = height;
    this.chosenWidget.widthWeight = width;
  }

  setSize(width, height) {
    this.data.chosenWidget.widthWeight = width;
    this.data.chosenWidget.heightWeight = height;
    this.data.refreshWidgets();
  }
}
